import React, { forwardRef, useContext, useMemo, useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { avatarToneFor } from "../../lib/avatar-tone.js";
import { SHAPE_CLASS } from "../../lib/shape.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { cn } from "../../lib/utils.js";
import { AvatarContext, type AvatarImageStatus } from "./avatar-context.js";

/* `size` is the square-media ramp xs/sm/md/lg (w-6/w-8/w-10/w-12, 24/32/40/
   48px, the steps IconTile shares). It is applied before `className`, so a
   caller passing an explicit `w-*`/`h-*` still overrides it. The base corner
   is `circle`, kept in the base string so the default renders today's exact
   classes; `shape="rounded"` appends the 8px corner, which cn() resolves over
   it. circle stays the default because an existing default keeps its look. */
export const avatarVariants = cva(
  `inline-flex items-center justify-center ${SHAPE_CLASS.circle} overflow-hidden shrink-0`,
  {
    variants: {
      size: {
        xs: "w-6 h-6",
        sm: "w-8 h-8",
        md: "w-10 h-10",
        lg: "w-12 h-12",
      },
      shape: {
        circle: "",
        rounded: SHAPE_CLASS.rounded,
      },
    },
    defaultVariants: { size: "md", shape: "circle" },
  },
);

/** The semantic tones an avatar takes. An avatar is never healthy or at risk. */
export type AvatarTone = Extract<StatusTone, "neutral" | "ai">;

type AvatarSize = "xs" | "sm" | "md" | "lg";

/* A painted avatar (seed or tone) sets its initials in semibold at one text
   step per size. */
const PAINTED_TEXT: Record<AvatarSize, string> = {
  xs: "text-dense-3",
  sm: "text-dense-1",
  md: "text-caption",
  lg: "text-body",
};

/* Teal marks the selected state only (C7): a state, never a tone. */
const SELECTED_RING = "ring-2 ring-pb-teal ring-offset-2 ring-offset-pb-card";

/* One paint per avatar: `seed` and `tone` together do not compile. */
type AvatarPaint =
  | {
      /** A stable id (or a name) that picks the person's gradient from
       *  `avatarToneFor` (d36), so one person looks the same everywhere. */
      seed?: string;
      tone?: never;
    }
  | {
      seed?: never;
      /** The soft fill of a semantic tone, with a 1px border in its colour. */
      tone?: AvatarTone;
    };

export type AvatarProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof avatarVariants> &
  AvatarPaint & {
    /** Adds the teal selection ring. */
    selected?: boolean;
    /** A small mark (presence dot, count) at the bottom-end corner. */
    badgeSlot?: React.ReactNode;
  };

function paintClass(
  seed: string | undefined,
  tone: AvatarTone | undefined,
): string | undefined {
  if (seed !== undefined) return avatarToneFor(seed);
  /* The tone's border colour rides in `soft`; the part sets the 1px width. */
  if (tone !== undefined) return cn("border", TONES[tone].soft);
  return undefined;
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(function Avatar(
  {
    size,
    shape,
    seed,
    tone,
    selected,
    badgeSlot,
    className,
    children,
    ...rest
  },
  ref,
) {
  const [imageStatus, setImageStatus] = useState<AvatarImageStatus>("idle");
  const paint = paintClass(seed, tone);
  const painted = paint !== undefined;
  const context = useMemo(
    () => ({ painted, imageStatus, onImageStatus: setImageStatus }),
    [painted, imageStatus],
  );
  const box = (
    <div
      ref={ref}
      className={cn(
        avatarVariants({ size, shape }),
        painted && ["font-semibold", PAINTED_TEXT[size ?? "md"], paint],
        selected && SELECTED_RING,
        className,
      )}
      {...rest}
    >
      <AvatarContext.Provider value={context}>
        {children}
      </AvatarContext.Provider>
    </div>
  );
  if (badgeSlot == null || typeof badgeSlot === "boolean") return box;
  return (
    <div className="relative inline-flex shrink-0">
      {box}
      <span className="absolute bottom-0 end-0 flex">{badgeSlot}</span>
    </div>
  );
});

/* Hidden once an AvatarImage has loaded. Inside a seeded or toned Avatar the
   text inherits the Avatar's paint; everywhere else it keeps text-pb-text. */
export const AvatarFallback = forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(function AvatarFallback({ className, children, ...rest }, ref) {
  const { painted, imageStatus } = useContext(AvatarContext);
  if (imageStatus === "loaded") return null;
  return (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center w-full h-full",
        !painted && "text-pb-text",
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
});
